import { useApiUserLineStatus } from '@hooks-api/wp/use-api-user-line-status'
import { useMemo } from 'react'

interface UseLineInvitationCheckOptions {
  /** 使用者帳號（email 或手機號碼，可能包含 + 前綴） */
  account?: string | null
  /** 是否啟用 LINE 邀請功能（對應 lineInvitation flag） */
  enabled?: boolean
}

export interface UseLineInvitationCheckReturn {
  /** 是否應該導向使用者至 LINE 邀請頁面 */
  shouldShowInvitation: boolean
  /** 是否正在檢查 LINE 綁定狀態 */
  isChecking: boolean
  /** 是否在冷卻期內 */
  isWithinThirtyDays: boolean
  /** 是否已綁定 LINE */
  lineBound: boolean
  /** 是否已加 LINE 好友 */
  lineFriend: boolean
}

/** 點選忍痛放棄後，冷卻 30 天 */
const PROMPT_COOLDOWN_SECONDS = 30 * 24 * 60 * 60

export function useLineInvitationCheck(
  props: UseLineInvitationCheckOptions,
): UseLineInvitationCheckReturn {
  const { account, enabled = false } = props

  /** 正規化 account */
  const normalizedAccount = useMemo(() => {
    if (!account) return null
    return account.startsWith('+') ? account.slice(1) : account
  }, [account])

  const { destructuredData: lineStatus, isLoading } = useApiUserLineStatus(
    enabled && normalizedAccount
      ? { identifier: normalizedAccount }
      : null,
  )

  const isWithinThirtyDays = useMemo(() => {
    if (lineStatus?.line_oa_friend_prompt_dismissed_at == null) return false
    const nowTimestamp = Math.floor(Date.now() / 1000)
    /** 尚未超過冷卻時間時間 */
    const secondsSinceDismissed = nowTimestamp - lineStatus.line_oa_friend_prompt_dismissed_at
    return secondsSinceDismissed < PROMPT_COOLDOWN_SECONDS
  }, [lineStatus])

  /** 是否應該導向使用者至 LINE 邀請頁面 */
  const shouldShowInvitation = useMemo(() => {
    if (!enabled || !lineStatus || isWithinThirtyDays) return false

    return !lineStatus.bound
  }, [enabled, lineStatus, isWithinThirtyDays])

  const lineBound = lineStatus?.bound ?? false
  const lineFriend = lineStatus?.friend ?? false

  return useMemo(() => ({
    shouldShowInvitation,
    isChecking: isLoading,
    isWithinThirtyDays,
    lineBound,
    lineFriend,
  }), [shouldShowInvitation, isLoading, isWithinThirtyDays, lineBound, lineFriend])
}
